import { useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { FileCheck, FileText, MoreHorizontal, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { formatDate, formatFileSize } from '@/lib/formatters'
import type { BookingFull, DocumentCheckItem, DocumentType, ReviewOutcome, ShipmentDocumentItem } from './types'
import {
  DOCUMENT_REVIEW_SORT_PRIORITY,
  compareDocumentsByRecency,
  documentTypeLabel,
  documentTypePillClassName,
  formatBookingMoveOption,
} from './helpers'
import DocumentUploadDialog from './DocumentUploadDialog'
import { LatestMetadataBadge, ReviewOutcomeSelect } from './DocumentReviewControls'

const DOCUMENT_CHECK_LABELS: Record<string, string> = {
  booking_ref_match: 'Booking ref',
  container_check_digit: 'Container #',
  bl_number_present: 'BL number',
  bl_vs_si_containers: 'BL vs SI containers',
}

function documentCheckLabel(code: string): string {
  return DOCUMENT_CHECK_LABELS[code] || code.replace(/_/g, ' ')
}

// Read-only, advisory display of the deterministic document checks. Shows only the
// actionable (fail/warn) checks as small chips; the evidence is the hover title.
function DocumentChecksInline({ checks }: { checks?: DocumentCheckItem[] }) {
  const actionable = (checks || []).filter((c) => c.status === 'fail' || c.status === 'warn')
  if (actionable.length === 0) return null

  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {actionable.map((check, i) => (
        <span
          key={`${check.code}-${i}`}
          title={check.evidence || [check.expected, check.actual].filter(Boolean).join(' → ') || undefined}
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
            check.status === 'fail' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
          }`}
        >
          {check.status === 'fail' ? '✕' : '⚠'} {documentCheckLabel(check.code)}
        </span>
      ))}
    </div>
  )
}

export default function DocumentsTab({ shipmentId, shipmentDocuments, bookings, pathFor, bookingFilterId, onBookingFilterChange }: {
  shipmentId: string
  shipmentDocuments: ShipmentDocumentItem[]
  bookings: BookingFull[]
  pathFor: (path: string) => string
  bookingFilterId: string | null
  onBookingFilterChange: (bookingId: string | null) => void
}) {
  const untaggedCount = shipmentDocuments.filter(d => d.untagged).length
  const unreviewedCount = shipmentDocuments.filter(d => d.review_outcome === 'unreviewed').length
  const selectedFilterBooking = bookings.find((booking) => booking.id === bookingFilterId) || null
  const [taggingDocId, setTaggingDocId] = useState<string | null>(null)
  const [docToDelete, setDocToDelete] = useState<ShipmentDocumentItem | null>(null)

  const filteredDocs = bookingFilterId
    ? shipmentDocuments.filter((document) => document.booking_id === bookingFilterId)
    : shipmentDocuments

  // Sort: untagged first, then review priority, then by created_at desc
  const sortedDocs = [...filteredDocs].sort((a, b) => {
    if (a.untagged && !b.untagged) return -1
    if (!a.untagged && b.untagged) return 1
    const aPriority = a.review_outcome ? DOCUMENT_REVIEW_SORT_PRIORITY[a.review_outcome] : 3
    const bPriority = b.review_outcome ? DOCUMENT_REVIEW_SORT_PRIORITY[b.review_outcome] : 3
    if (aPriority !== bPriority) return aPriority - bPriority
    return compareDocumentsByRecency(a, b)
  })

  const [showUploadModal, setShowUploadModal] = useState(false)

  function handleTagToBooking(docId: string, bookingId: string | null) {
    router.patch(pathFor(`/shipment_documents/${docId}`), {
      booking_id: bookingId,
    }, {
      preserveState: true,
      onSuccess: () => {
        setTaggingDocId(null)
      },
      onError: () => {
        toast.error('Failed to update document')
      }
    })
  }

  function handleDocumentTypeChange(docId: string, documentType: DocumentType) {
    router.patch(pathFor(`/shipment_documents/${docId}`), {
      document_type: documentType,
    }, {
      preserveState: true,
      onError: () => {
        toast.error('Failed to update document type')
      }
    })
  }

  function handleReviewOutcomeChange(docId: string, reviewOutcome: ReviewOutcome) {
    router.patch(pathFor(`/shipment_documents/${docId}`), {
      review_outcome: reviewOutcome,
    }, {
      preserveState: true,
      onError: () => {
        toast.error('Failed to update document review')
      }
    })
  }

  function handleDeleteDocument() {
    if (!docToDelete) return

    router.delete(pathFor(`/shipment_documents/${docToDelete.id}`), {
      preserveState: true,
      onSuccess: () => {
        setDocToDelete(null)
      },
      onError: () => {
        toast.error('Failed to delete document')
        setDocToDelete(null)
      }
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[15px] font-semibold text-foreground">
          Documents ({shipmentDocuments.length})
          {untaggedCount > 0 && (
            <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700">
              {untaggedCount} untagged
            </Badge>
          )}
          {unreviewedCount > 0 && (
            <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700">
              {unreviewedCount} unreviewed
            </Badge>
          )}
        </h3>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] text-muted-foreground hover:text-foreground font-medium cursor-pointer"
        >
          <Plus className="h-3 w-3" /> Upload Document
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <label className="text-[11px] font-semibold uppercase text-muted-foreground">Booking:</label>
          <select
            aria-label="Filter documents by booking"
            value={bookingFilterId || ''}
            onChange={(event) => onBookingFilterChange(event.target.value || null)}
            className="h-7 min-w-[220px] rounded border border-gray-200 bg-white px-2 text-[12px] cursor-pointer"
          >
            <option value="">All bookings</option>
            {bookings.map((booking) => (
              <option key={booking.id} value={booking.id}>
                {formatBookingMoveOption(booking)}
              </option>
            ))}
          </select>
        </div>
        <span className="text-[11px] text-muted-foreground">
          {selectedFilterBooking
            ? `Showing ${filteredDocs.length} of ${shipmentDocuments.length} for ${selectedFilterBooking.internal_reference || selectedFilterBooking.carrier_booking_id || 'selected booking'}`
            : `Showing all ${shipmentDocuments.length}`}
        </span>
      </div>

      {filteredDocs.length > 0 ? (
        <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
          <div className="hidden border-b bg-muted/40 px-3 py-2 text-[10px] font-medium text-muted-foreground lg:grid lg:grid-cols-[56px_minmax(0,1fr)_112px_80px_minmax(170px,240px)_120px_32px] lg:items-center lg:gap-3 uppercase">
            <span>Type</span>
            <span>Filename</span>
            <span>Date</span>
            <span>Size</span>
            <span>Booking</span>
            <span>Review</span>
            <span />
          </div>
          <div className="divide-y">
            {sortedDocs.map((doc) => (
              <UnifiedDocumentRow
                key={doc.id}
                doc={doc}
                bookings={bookings}
                pathFor={pathFor}
                taggingDocId={taggingDocId}
                setTaggingDocId={setTaggingDocId}
                onTagToBooking={handleTagToBooking}
                onDelete={() => setDocToDelete(doc)}
                onDocumentTypeChange={handleDocumentTypeChange}
                onReviewOutcomeChange={handleReviewOutcomeChange}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-gray-200 px-3 py-6 text-center text-[13px] text-muted-foreground">
          {selectedFilterBooking ? 'No documents for this booking.' : 'No documents attached to this shipment.'}
        </div>
      )}

      {showUploadModal && (
        <DocumentUploadDialog
          open={showUploadModal}
          onOpenChange={setShowUploadModal}
          shipmentId={shipmentId}
          bookings={bookings}
          pathFor={pathFor}
        />
      )}

      <AlertDialog open={!!docToDelete} onOpenChange={(open) => !open && setDocToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{docToDelete?.filename || (docToDelete ? documentTypeLabel(docToDelete.document_type) : 'document')}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDocToDelete(null)} className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDocument} className="bg-red-600 hover:bg-red-700 cursor-pointer">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface UnifiedDocumentRowProps {
  doc: ShipmentDocumentItem
  bookings: BookingFull[]
  pathFor: (path: string) => string
  taggingDocId: string | null
  setTaggingDocId: (id: string | null) => void
  onTagToBooking: (docId: string, bookingId: string | null) => void
  onDelete: () => void
  onDocumentTypeChange: (docId: string, documentType: DocumentType) => void
  onReviewOutcomeChange: (docId: string, reviewOutcome: ReviewOutcome) => void
}

function UnifiedDocumentRow({
  doc,
  bookings,
  pathFor,
  taggingDocId,
  setTaggingDocId,
  onTagToBooking,
  onDelete,
  onDocumentTypeChange,
  onReviewOutcomeChange,
}: UnifiedDocumentRowProps) {
  const [showMenu, setShowMenu] = useState(false)
  const [changingDocType, setChangingDocType] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
        setChangingDocType(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handleDownload() {
    if (doc.attachment_id) {
      window.open(pathFor(`/attachments/${doc.attachment_id}/download`), '_blank')
    }
    setShowMenu(false)
  }

  function handleUntag() {
    onTagToBooking(doc.id, null)
    setShowMenu(false)
  }

  function handleTypeChange(documentType: DocumentType) {
    onDocumentTypeChange(doc.id, documentType)
    setChangingDocType(false)
    setShowMenu(false)
  }

  const booking = bookings.find(b => b.id === doc.booking_id)
  const Icon = doc.document_type === 'bl' ? FileCheck : FileText
  const title = doc.filename || doc.document_number || documentTypeLabel(doc.document_type)

  return (
    <div
      data-shipment-document-row
      className={`grid items-center gap-3 px-3 py-2.5 lg:grid-cols-[56px_minmax(0,1fr)_112px_80px_minmax(170px,240px)_120px_32px] ${doc.untagged ? 'bg-red-50/50' : 'hover:bg-muted/20'}`}
    >
      <span className={`inline-flex h-5 w-12 items-center justify-center rounded-full text-[10px] font-semibold ${documentTypePillClassName(doc.document_type)}`}>
        {documentTypeLabel(doc.document_type)}
      </span>

      <div className="min-w-0">
        <button
          onClick={handleDownload}
          className="flex max-w-full items-center gap-2 cursor-pointer group"
          disabled={!doc.attachment_id}
        >
          <Icon className={`h-3.5 w-3.5 shrink-0 ${doc.is_latest ? 'text-blue-600' : 'text-muted-foreground'}`} />
          <span className="min-w-0 truncate text-[12px] font-medium text-foreground hover:underline">
            {title}
          </span>
          {doc.is_latest && <LatestMetadataBadge />}
        </button>
        <DocumentChecksInline checks={doc.checks} />
      </div>

      <div className="text-[11px] text-muted-foreground">
        <div className="mb-1 text-[10px] font-medium uppercase leading-none text-muted-foreground lg:hidden">Date</div>
        {formatDate(doc.created_at)}
      </div>

      <div className="text-[11px] text-muted-foreground">
        <div className="mb-1 text-[10px] font-medium uppercase leading-none text-muted-foreground lg:hidden">Size</div>
        {doc.size ? formatFileSize(doc.size) : '-'}
      </div>

      <div>
        <div className="mb-1 text-[10px] font-medium uppercase leading-none text-muted-foreground lg:hidden">Booking</div>
        {taggingDocId === doc.id ? (
          <select
            aria-label={`Booking assignment for ${doc.filename || doc.id}`}
            className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 cursor-pointer"
            value={doc.booking_id || ''}
            onChange={(e) => onTagToBooking(doc.id, e.target.value || null)}
            autoFocus
            onBlur={() => setTaggingDocId(null)}
          >
            <option value="">Untagged</option>
            {bookings.map(b => (
              <option key={b.id} value={b.id}>
                {formatBookingMoveOption(b)}
              </option>
            ))}
          </select>
        ) : doc.booking_id ? (
          <button
            type="button"
            onClick={() => setTaggingDocId(doc.id)}
            className="max-w-full truncate text-left text-[11px] font-medium text-gray-700 hover:text-blue-600 hover:underline cursor-pointer"
          >
            {booking ? formatBookingMoveOption(booking) : doc.carrier_booking_id || 'Tagged booking'}
          </button>
        ) : (
          <button
            onClick={() => setTaggingDocId(doc.id)}
            className="text-[11px] text-red-600 hover:underline cursor-pointer border border-red-300 rounded px-2 py-0.5"
          >
            Tag to Booking
          </button>
        )}
      </div>

      <div>
        <div className="mb-1 text-[10px] font-medium uppercase leading-none text-muted-foreground lg:hidden">Review</div>
        <div className="flex items-center gap-1.5">
          {doc.document_type === 'other' ? (
            <span className="text-[11px] text-muted-foreground">-</span>
          ) : doc.untagged ? (
            <span className="text-[11px] text-muted-foreground">Tag first</span>
          ) : (
            <ReviewOutcomeSelect
              outcome={doc.review_outcome}
              onChange={(reviewOutcome) => onReviewOutcomeChange(doc.id, reviewOutcome)}
            />
          )}
        </div>
      </div>

      <div className="relative justify-self-start lg:justify-self-end" ref={menuRef}>
        <button
          onClick={() => {
            if (showMenu) setChangingDocType(false)
            setShowMenu(!showMenu)
          }}
          className="flex h-5 w-5 items-center justify-center rounded border border-gray-200 text-gray-400 hover:border-gray-400 hover:text-gray-600 cursor-pointer"
          title="More actions"
        >
          <MoreHorizontal className="h-3 w-3" />
        </button>

        {showMenu && (
          <div className="absolute right-0 z-20 mt-1 w-44 rounded-md border bg-white py-0.5 shadow-lg">
            <div>
              {changingDocType ? (
                <div className="px-2 py-1">
                  <select
                    aria-label={`Document type for ${title}`}
                    value={doc.document_type}
                    onChange={(event) => handleTypeChange(event.target.value as DocumentType)}
                    onBlur={() => {
                      setChangingDocType(false)
                      setShowMenu(false)
                    }}
                    className="w-full rounded border px-1.5 py-0.5 text-[11px] cursor-pointer"
                    autoFocus
                  >
                    <option value="si">SI</option>
                    <option value="bl">BL</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              ) : (
                <>
                  {doc.booking_id ? (
                    <button
                      onClick={() => {
                        setTaggingDocId(doc.id)
                        setShowMenu(false)
                      }}
                      className="w-full px-3 py-1 text-left text-[11px] hover:bg-gray-100 cursor-pointer"
                    >
                      Move to Booking
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setTaggingDocId(doc.id)
                        setShowMenu(false)
                      }}
                      className="w-full px-3 py-1 text-left text-[11px] hover:bg-gray-100 cursor-pointer"
                    >
                      Tag to Booking
                    </button>
                  )}
                  <button
                    onClick={() => setChangingDocType(true)}
                    className="w-full px-3 py-1 text-left text-[11px] hover:bg-gray-100 cursor-pointer"
                  >
                    Change type
                  </button>
                  {doc.booking_id && (
                    <button
                      onClick={handleUntag}
                      className="w-full px-3 py-1 text-left text-[11px] hover:bg-gray-100 cursor-pointer"
                    >
                      Untag
                    </button>
                  )}
                  <button
                    onClick={() => {
                      onDelete()
                      setShowMenu(false)
                    }}
                    className="w-full px-3 py-1 text-left text-[11px] text-red-600 hover:bg-gray-100 cursor-pointer"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
